import axios from 'axios';
import { tauriAxiosAdapter } from '../utils/tauriAxiosAdapter';
import { attachAuthHeaders } from '../utils/clientId';
import { isNetworkError } from '../utils/networkError';
import {
  ensureFreshToken,
  refreshNow,
  hasRefreshableSession,
  clearTokens,
} from './tokenRefresher';

// Create axios instance with base configuration
const api = axios.create({
  // Fallback must match apiClient.js — the versioned API root. The old
  // unversioned "/api" fallback only worked via the backend's 308 redirect.
  baseURL: process.env.REACT_APP_API_BASE_URL || 'https://api.apiboo.com/api/v1',
  timeout: 10000,
  adapter: tauriAxiosAdapter,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: proactive refresh when the access token is about to
// expire (single-flight inside tokenRefresher), then attach headers.
api.interceptors.request.use(
  async (config) => {
    await ensureFreshToken().catch(() => {});
    return attachAuthHeaders(config);
  },
  (error) => Promise.reject(error)
);

// Response interceptor: one refresh + one retry on 401, then a logout.
//
// This is a second, independent axios instance from utils/apiClient.js's,
// with its OWN 401 handling — a second, undocumented logout path that used
// to do `clearTokens(); window.location.reload();` here instead of what
// apiClient.js's interceptor does. That reload() gave the JS layer a false
// sense of a clean slate while actually skipping the shared teardown
// entirely: no disconnectSocket() (so the native sync engine was never told
// this session ended — see channelService.js/syncAuthBridge.js), and if
// `logout()` below is what 401'd (its very first line is `authService.
// logout()`, i.e. THIS interceptor), the reload used to cut `logout()` off
// mid-function before any of its own teardown ran at all. Dispatching the
// same `auth:logout` event apiClient.js's soft path dispatches routes this
// path through the exact same shared teardown instead of reinventing a
// thinner one — see plans/01_08_2026/outstanding/01-session-lifecycle-owner.md.
api.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    // Card X1: a device removed from the vault's list is not a dead session.
    if (error.response?.status === 401 && error.response?.data?.error === 'device_revoked') return Promise.reject(error);
    if (error.response?.status === 401 && localStorage.getItem('authToken')) {
      const config = error.config || {};

      if (!config._authRetried && hasRefreshableSession()) {
        try {
          await refreshNow();
          config._authRetried = true;
          // Re-run through the instance so headers are re-attached and the
          // response mapping (`response.data`) applies to the retry too.
          return api.request(config);
        } catch (refreshError) {
          // A refresh that failed because the IdP was UNREACHABLE says
          // nothing about the refresh token — it is still valid, and
          // clearTokens() below would throw away the only thing that can
          // recover this session. Leave the tokens alone and let the caller
          // see the original 401; the next attempt, on a working network,
          // refreshes normally.
          if (isNetworkError(refreshError)) {
            return Promise.reject(error);
          }
          // fall through to logout
        }
      }

      clearTokens();
      window.dispatchEvent(new CustomEvent('auth:logout'));
    }
    return Promise.reject(error);
  }
);

// Auth service
export const authService = {
  login: (credentials) => api.post('/auth/login', credentials),
  // Exchange a GitHub OAuth token for an Apiboo session (device-flow sign in).
  githubLogin: (token) => api.post('/auth/github', { token }),
  // OAuth web flow: the browser-opened start URL (with the app's loopback
  // redirect), and the handoff-code exchange.
  githubStartUrl: (state, appRedirect) => {
    const params = new URLSearchParams({ state });
    if (appRedirect) params.set('app_redirect', appRedirect);
    return `${api.defaults.baseURL}/auth/github/start?${params.toString()}`;
  },
  githubExchange: (code, state) => api.post('/auth/github/exchange', { code, state }),
  register: (userData) => api.post('/auth/register', userData),
  logout: () => api.post('/auth/logout'),
  // Terminal (CLI/CI) token: shown once, survives app logins, and a new one
  // replaces the previous terminal session.
  createCliToken: () => api.post('/auth/cli-token'),
  getUser: () => api.get('/auth/user'),

  /**
   * Redeem a promotion code, which GRANTS a plan rather than discounting one:
   * the server sets `plan` and `plan_expires_at` and creates no Stripe
   * subscription (Admin.redeem_promotion/2). So there is nothing to pay and no
   * card to enter — which is why this lives next to "Upgrade plan" rather than
   * in a checkout flow.
   *
   * The server answers 422 with one message for an unknown, disabled, expired
   * OR exhausted code, deliberately: distinguishing them would tell a guesser
   * which codes exist. Callers should show that message as-is instead of
   * inventing a more specific one.
   */
  redeemPromotion: (code) => api.post('/promotions/redeem', { code }),
  updateProfile: (userData) => api.put('/auth/profile', userData),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: ({ email, token, password }) =>
    api.post('/auth/reset-password', { email, token, password }),
  verifyEmail: ({ email, token }) => api.post('/auth/verify-email', { email, token }),
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
  getUsage: () => api.get('/auth/usage'),
  // Plans v2: the signed plan claim (offline local caps), verified in Rust.
  getPlanClaim: () => api.get('/me/plan-claim'),
  cancelPlan: () => api.delete('/auth/plan'),
  reactivatePlan: () => api.post('/auth/plan/reactivate'),
  // Card 3.7: `include` = the categories the person chose (none = the server's defaults).
  // `account` always leads, so "nothing else ticked" is never an empty list
  // (which the server reads as its defaults).
  exportData: (include) =>
    api.get('/auth/export', include ? { params: { include: ['account', ...include].join(',') } } : undefined),
  deleteAccount: (password) => api.delete('/auth/account', { data: { password } }),

  // TOTP / 2FA
  totpSetup: () => api.post('/auth/totp/setup'),
  totpEnable: (secret, code) => api.post('/auth/totp/enable', { secret, code }),
  totpVerify: (partialToken, code) => api.post('/auth/totp/verify', { partial_token: partialToken, code }),
  totpDisable: (password) => api.delete('/auth/totp', { data: { password } }),

  // Passkeys (WebAuthn)
  passkeyRegisterBegin: () => api.post('/auth/passkey/register/begin'),
  passkeyRegisterComplete: (data) => api.post('/auth/passkey/register/complete', data),
  passkeyAuthBegin: (partialToken) => api.post('/auth/passkey/authenticate/begin', { partial_token: partialToken }),
  passkeyAuthComplete: (data) => api.post('/auth/passkey/authenticate/complete', data),
  listPasskeys: () => api.get('/auth/passkeys'),
  deletePasskey: (id) => api.delete(`/auth/passkeys/${id}`),

  // Email login code
  emailCodeSend: (partialToken) => api.post('/auth/email-code/send', { partial_token: partialToken }),
  emailCodeVerify: (partialToken, code) => api.post('/auth/email-code/verify', { partial_token: partialToken, code }),
};

export default api;
